// core/google-auth.js — Google Identity Services (GIS) loader e parser de ID token.
//
// Responsabilidades:
//   - Carregar o script do GIS (https://accounts.google.com/gsi/client) sob demanda
//   - Inicializar google.accounts.id com o Client ID do .env
//   - Renderizar botão "Entrar com Google" ou disparar prompt programático
//   - Decodificar o ID token (JWT) no frontend para uso imediato (email, nome, sub)
//
// O que NÃO faz (delegado a outros módulos):
//   - Validar assinatura do token (feito no backend via tokeninfo do Google)
//   - Persistir sessão (core/session.js)
//   - Decidir se o usuário é admin/aluno (core/auth.js)
//
// Referência: https://developers.google.com/identity/gsi/web/guides/overview

const GIS_SCRIPT_SRC = 'https://accounts.google.com/gsi/client';
let gisReady = null; // Promise singleton
let initialized = false;

/**
 * Injeta o script do GIS e resolve quando window.google.accounts.id existir.
 * Chamadas repetidas retornam a mesma promise.
 */
export function loadGIS() {
  if (gisReady) return gisReady;

  gisReady = new Promise((resolve, reject) => {
    // Já carregado em outra instância?
    if (window.google?.accounts?.id) {
      resolve(window.google.accounts.id);
      return;
    }

    const existing = document.querySelector(`script[src="${GIS_SCRIPT_SRC}"]`);
    if (existing) {
      existing.addEventListener('load', () => {
        if (window.google?.accounts?.id) resolve(window.google.accounts.id);
        else reject(new Error('[google-auth] GIS carregou mas API não disponível'));
      }, { once: true });
      existing.addEventListener('error', () => reject(new Error('[google-auth] falha ao carregar GIS')), { once: true });
      return;
    }

    const s = document.createElement('script');
    s.src = GIS_SCRIPT_SRC;
    s.async = true;
    s.defer = true;
    s.onload = () => {
      if (window.google?.accounts?.id) resolve(window.google.accounts.id);
      else reject(new Error('[google-auth] GIS carregou mas API não disponível'));
    };
    s.onerror = () => reject(new Error('[google-auth] falha ao carregar GIS (verifique rede)'));
    document.head.appendChild(s);
  });

  return gisReady;
}

/**
 * Inicializa google.accounts.id com o Client ID e um callback.
 * Idempotente: chamadas extras são ignoradas.
 *
 * @param {(credential: string) => void} onCredential - recebe o ID token (JWT)
 */
export async function initGoogleAuth(onCredential) {
  const clientId = import.meta.env.VITE_GOOGLE_CLIENT_ID;
  if (!clientId || clientId.includes('seu-client-id-aqui')) {
    throw new Error('[google-auth] VITE_GOOGLE_CLIENT_ID não configurado em .env');
  }
  if (typeof onCredential !== 'function') {
    throw new Error('[google-auth] initGoogleAuth requer callback onCredential');
  }

  const id = await loadGIS();
  if (initialized) return id;

  id.initialize({
    client_id: clientId,
    callback: (response) => {
      if (response?.credential) onCredential(response.credential);
      else console.warn('[google-auth] callback sem credential', response);
    },
    auto_select: false,
    cancel_on_tap_outside: true,
    use_fedcm_for_prompt: true, // Chrome FedCM (recomendado desde out/2024)
  });

  initialized = true;
  return id;
}

/**
 * Renderiza o botão oficial "Entrar com Google" dentro de um elemento container.
 * O container é limpo antes da renderização.
 *
 * @param {HTMLElement} container
 * @param {{theme?: 'outline'|'filled_blue', size?: 'large'|'medium', text?: 'signin_with'|'continue_with', width?: number}} opts
 */
export async function renderGoogleButton(container, opts = {}) {
  if (!container || !(container instanceof HTMLElement)) {
    throw new Error('[google-auth] renderGoogleButton requer HTMLElement');
  }
  const id = await loadGIS();
  container.innerHTML = '';
  id.renderButton(container, {
    theme: opts.theme || 'filled_blue',
    size: opts.size || 'large',
    text: opts.text || 'signin_with',
    shape: 'pill',
    locale: 'pt-BR',
    width: opts.width || 280,
  });
}

/**
 * Dispara o One Tap prompt programaticamente.
 * Útil para "tente de novo" quando o usuário cancelou.
 */
export async function promptOneTap() {
  const id = await loadGIS();
  id.prompt();
}

/**
 * Revoga o token atual e desabilita auto_select.
 * Chamado no logout.
 */
export async function disableAutoSelect() {
  try {
    const id = await loadGIS();
    id.disableAutoSelect();
  } catch (e) {
    // silencioso — logout deve ser idempotente mesmo sem GIS
  }
}

/**
 * Decodifica o payload de um ID token (JWT) sem validar assinatura.
 * Validação de assinatura é feita no backend.
 *
 * @param {string} jwt
 * @returns {{email:string, email_verified:boolean, name:string, given_name:string, picture:string, sub:string, exp:number}|null}
 */
export function decodeIdToken(jwt) {
  try {
    if (typeof jwt !== 'string' || !jwt.includes('.')) return null;
    const payloadB64 = jwt.split('.')[1];
    // base64url → base64
    const b64 = payloadB64.replace(/-/g, '+').replace(/_/g, '/');
    const pad = b64.length % 4;
    const padded = pad ? b64 + '='.repeat(4 - pad) : b64;
    const json = decodeURIComponent(
      atob(padded)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(json);
  } catch (e) {
    console.error('[google-auth] decodeIdToken falhou:', e);
    return null;
  }
}

/**
 * Testa se um ID token ainda é válido pelo claim `exp` (sem ir ao servidor).
 * Usado como fast-path no boot; validação real sempre no backend.
 */
export function isTokenExpired(jwt) {
  const payload = decodeIdToken(jwt);
  if (!payload?.exp) return true;
  return Date.now() / 1000 >= payload.exp;
}
